import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Boxes, Calculator, Tag, CalendarClock, MapPin, Banknote, AlertTriangle, Layers,
  ArrowRight, Package, Truck, Bell, Check, Building2, Store, Briefcase, User, Factory,
  ShoppingBag, Globe2, Warehouse, Handshake,
} from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, GradientCta, fadeUp } from "../components/marketing/Blocks";

const FEATURES = [
  { icon: Boxes, title: "Smart Courier Selection", desc: "Choose the right courier based on price, delivery speed, serviceability and shipment requirements." },
  { icon: Calculator, title: "Real-Time Rate Comparison", desc: "Compare available courier rates before booking a shipment." },
  { icon: Tag, title: "Automated Shipping Labels", desc: "Generate professional shipping labels quickly without manual work." },
  { icon: CalendarClock, title: "Pickup Management", desc: "Schedule and manage courier pickups directly from the ShipAny dashboard." },
  { icon: MapPin, title: "Real-Time Tracking", desc: "Track shipments from pickup to final delivery." },
  { icon: Banknote, title: "COD Management", desc: "Manage cash-on-delivery shipments and monitor COD-related information." },
  { icon: AlertTriangle, title: "NDR Management", desc: "Identify failed deliveries and take action to improve successful delivery rates." },
  { icon: Layers, title: "Bulk Shipping", desc: "Upload and process large numbers of shipments quickly." },
];

const WORKFLOW = ["Add Order", "Compare Couriers", "Select Courier", "Generate Label", "Track Delivery"];

const AUDIENCES = [
  { icon: ShoppingBag, title: "D2C Brands" }, { icon: Store, title: "E-commerce Sellers" },
  { icon: Globe2, title: "Marketplaces" }, { icon: Warehouse, title: "Retailers" },
  { icon: Factory, title: "Manufacturers" }, { icon: Briefcase, title: "SMEs" },
  { icon: Building2, title: "Enterprises" }, { icon: Handshake, title: "Social Commerce Sellers" },
];

const WHY = [
  "Multiple courier options", "Competitive shipping rates", "Centralized shipping dashboard",
  "Faster booking", "Automated operations", "Tracking visibility",
  "COD support", "NDR management", "Bulk shipment processing",
];

function HeroVisual() {
  return (
    <div className="relative mx-auto mt-12 aspect-[16/10] max-w-lg sm:aspect-[16/9]" data-testid="domestic-hero-visual">
      <div className="absolute inset-0 flex items-center justify-center">
        <img src="/maps/india.svg" alt="India shipping coverage map" className="h-full w-auto opacity-70" />
      </div>
      <svg viewBox="0 0 100 62" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <path d="M 20 40 Q 35 18 52 30 T 80 22" fill="none" stroke="#12C8F5" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" />
        <path d="M 24 44 Q 40 50 58 42 T 78 34" fill="none" stroke="#FF9933" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" style={{ animationDuration: "7s" }} />
        <path d="M 30 36 Q 46 26 64 36" fill="none" stroke="#10B981" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" style={{ animationDuration: "9s" }} />
      </svg>
      <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-[6%] top-[16%] flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 shadow-xl backdrop-blur-xl">
        <Package className="h-6 w-6 text-[#FF9933]" />
      </motion.div>
      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[14%] right-[8%] flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 shadow-xl backdrop-blur-xl">
        <Truck className="h-6 w-6 text-[#10B981]" />
      </motion.div>
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-[42%] top-[34%] flex h-12 w-12 items-center justify-center rounded-full border border-shipany-cyan/40 bg-shipany-cyan/15 shadow-xl backdrop-blur-xl">
        <MapPin className="h-5 w-5 text-shipany-cyan" />
      </motion.div>
      {[[30, 30], [58, 24], [70, 44], [42, 52]].map(([x, y], i) => (
        <span key={i} className="pulse-dot absolute h-2.5 w-2.5 rounded-full bg-[#FF9933]" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.4}s` }} />
      ))}
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-[2%] top-[4%] w-44 rounded-2xl border border-white/15 bg-[#071B3A]/90 p-3.5 shadow-2xl backdrop-blur-xl">
        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">ShipAny dashboard</p>
        <div className="mt-2 space-y-1.5">
          {[["3 couriers compared", "text-shipany-cyan"], ["Label generated", "text-emerald-400"], ["Pickup scheduled", "text-slate-300"]].map(([t, c]) => (
            <div key={t} className="flex items-center gap-1.5 text-[10px] font-semibold"><Check className={`h-3 w-3 ${c}`} /><span className="text-slate-200">{t}</span></div>
          ))}
        </div>
      </motion.div>
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[4%] left-[2%] flex w-48 items-center gap-2.5 rounded-2xl border border-white/15 bg-[#071B3A]/90 p-3 shadow-2xl backdrop-blur-xl">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#10B981]/20"><Bell className="h-4 w-4 text-[#10B981]" /></span>
        <div><p className="text-[10px] font-bold text-white">Out for delivery</p><p className="text-[9px] text-slate-400">Track every step live</p></div>
      </motion.div>
    </div>
  );
}

export default function Domestic() {
  return (
    <MarketingLayout>
      <div data-testid="domestic-shipping-page">
        <MarketingHero kicker="Domestic Shipping" title="Powerful Shipping Infrastructure for Modern Indian Businesses"
          sub="ShipAny helps businesses manage domestic shipments through multiple courier partners from one powerful platform. Compare rates, select the right courier, generate shipping labels, track orders, manage COD and handle the complete delivery lifecycle from a single dashboard.">
          <motion.div {...fadeUp} className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link to="/signup" data-testid="domestic-start-shipping-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Start Shipping <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/#rate-calculator" data-testid="domestic-calc-rates-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
              Calculate Shipping Rates <Calculator className="h-4 w-4" />
            </Link>
          </motion.div>
          <HeroVisual />
        </MarketingHero>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="domestic-features-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Capabilities" title="Everything You Need to Ship" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {FEATURES.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
                  data-testid={`domestic-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20 transition-transform duration-300 group-hover:scale-110">
                    <f.icon className="h-5 w-5 text-shipany-cyan" />
                  </span>
                  <h3 className="font-display mt-4 text-sm font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="domestic-workflow-section">
          <div className="mx-auto max-w-6xl px-5">
            <SectionHead kicker="Workflow" title="How ShipAny Shipping Works" />
            <div className="relative mt-16" data-testid="domestic-workflow-track">
              <div className="absolute left-0 right-0 top-6 hidden h-0.5 rounded bg-white/10 md:block" />
              <motion.div animate={{ left: ["3%", "88%"] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }}
                className="absolute -top-2 z-10 hidden h-9 w-9 items-center justify-center rounded-xl border border-[#FF9933]/50 bg-[#071B3A] shadow-[0_6px_20px_rgba(255,153,51,0.35)] md:flex"
                data-testid="domestic-workflow-package">
                <Package className="h-4 w-4 text-[#FF9933]" />
              </motion.div>
              <div className="grid gap-8 md:grid-cols-5">
                {WORKFLOW.map((step, i) => (
                  <motion.div key={step} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.12 }} className="relative flex flex-col items-center text-center"
                    data-testid={`domestic-step-${i + 1}`}>
                    <span className="relative z-[5] flex h-12 w-12 items-center justify-center rounded-full border border-shipany-cyan/40 bg-[#0B1E3D] font-display text-sm font-bold text-shipany-cyan">
                      {i + 1}
                    </span>
                    <p className="mt-3 text-sm font-bold text-white">{step}</p>
                    {i < WORKFLOW.length - 1 && <ArrowRight className="mt-2 h-4 w-4 text-slate-600 md:hidden" />}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="domestic-audience-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Who it's for" title="Built for Every Business" />
            <div className="mt-12 grid grid-cols-2 gap-5 md:grid-cols-4">
              {AUDIENCES.map((a, i) => (
                <motion.div key={a.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
                  data-testid={`domestic-audience-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center transition-all duration-300 hover:-translate-y-1.5 hover:border-[#FF9933]/50">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FF9933]/15"><a.icon className="h-5 w-5 text-[#FF9933]" /></span>
                  <h3 className="font-display mt-4 text-sm font-bold text-white">{a.title}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="domestic-why-section">
          <div className="mx-auto max-w-5xl px-5">
            <SectionHead kicker="Why ShipAny" title="Why Businesses Choose ShipAny" />
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {WHY.map((w, i) => (
                <motion.div key={w} {...fadeUp} transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                  data-testid={`domestic-why-${w.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#10B981]/15">
                    <Check className="h-4 w-4 text-[#10B981]" />
                  </span>
                  <span className="text-sm font-semibold text-slate-200">{w}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <GradientCta title="Ready to Ship Smarter?" sub="Start managing your shipments with a smarter, more flexible shipping platform."
          primary={["Start Shipping", "/signup"]} secondary={["Talk to Sales", "/contact"]} testId="domestic-final-cta" />
      </div>
    </MarketingLayout>
  );
}
